import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { SEO } from "@/lib/seo";

const fieldClass = "w-full rounded-xl border border-border bg-card px-4 py-3 text-foreground outline-none transition placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/25";
const labelClass = "mb-2 block text-sm font-bold text-foreground";

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("access_token")) {
      setReady(true);
    } else {
      supabase.auth.getSession().then(({ data }) => setReady(Boolean(data.session)));
    }
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t("resetPassword.mismatch")); return; }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(t("resetPassword.success"));
    window.history.replaceState(null, "", "/auth");
    navigate("/auth");
  };

  return (
    <section className="min-h-screen bg-hero-gradient ambient-grid px-4 py-12 sm:px-6 lg:px-8">
      <SEO title={t("resetPassword.title")} description={t("resetPassword.title")} path="/reset-password" noindex />
      <div className="mx-auto max-w-md">
        <h1 className="mb-6 text-center text-3xl font-black">{t("resetPassword.title")}</h1>
        <form onSubmit={onSubmit} className="rounded-2xl border border-border bg-background/90 p-6 shadow-soft backdrop-blur">
          {!ready ? (
            <p className="text-center text-sm font-bold text-destructive">{t("resetPassword.invalidLink")}</p>
          ) : (
            <>
              <label className="block">
                <span className={labelClass}>{t("resetPassword.newPassword")}</span>
                <input className={fieldClass} type="password" minLength={6} required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              </label>
              <label className="mt-4 block">
                <span className={labelClass}>{t("resetPassword.confirm")}</span>
                <input className={fieldClass} type="password" minLength={6} required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
              </label>
              <button disabled={busy} type="submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-black text-primary-foreground transition hover:scale-[1.01]">
                <KeyRound className="h-4 w-4" /> {t("resetPassword.save")}
              </button>
            </>
          )}
          <p className="mt-6 text-center text-sm">
            <Link to="/auth" className="font-bold text-primary hover:underline">← {t("resetPassword.back")}</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default ResetPassword;
